import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  MessageSquare,
  X,
  Save,
  AlertTriangle,
  Loader2,
  RefreshCw,
  MoreHorizontal,
  Columns3,
  FileText,
  ChevronLeft,
  CheckCircle2,
} from "lucide-react";
import VociGrid from "../../components/grid/VociGrid";
import IpotesiSoftwareCard from "../../components/IpotesiSoftwareCard";
import { EmptyWorkbench } from "../../components/illustrations/EmptyIllustrations";
import BlocchiClienteBanner from "../../components/BlocchiClienteBanner";
import ListinoAlternativesPopover from "../../components/ListinoAlternativesPopover";
import LearningRuleModal from "../../components/workbench/LearningRuleModal";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import StatusStepBar from "../../components/StatusStepBar";
import ApprovalBanner from "../../components/ApprovalBanner";
import PdfPreviewModal from "../../components/PdfPreviewModal";
import { useAuth, hasRole } from "../../context/AuthContext";
import { offersApi } from "../../lib/offersApi";
import { api } from "../../lib/api";

const STATUS_LABEL = {
  analyzing: "in analisi",
  awaiting_info: "info da richiedere",
  matching: "in lavorazione",
  completed: "completata",
};

function ToolbarBtn({ children, onClick, disabled, variant = "ghost", testId }) {
  const styles = {
    primary: {
      background: disabled ? "var(--bg-subtle)" : "var(--accent-primary)",
      color: disabled ? "var(--text-muted)" : "var(--text-on-accent)",
      border: "1px solid transparent",
      boxShadow: disabled ? "none" : "var(--shadow-sm)",
    },
    secondary: {
      background: "var(--bg-surface)",
      color: "var(--text-primary)",
      border: "1px solid var(--border-default)",
    },
    ghost: {
      background: "transparent",
      color: "var(--text-secondary)",
      border: "1px solid transparent",
    },
    warning: {
      background: "#FFFBEB",
      color: "#B45309",
      border: "1px solid #FDE68A",
    },
  }[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[12px] font-medium transition-all disabled:cursor-not-allowed whitespace-nowrap"
      style={styles}
      onMouseEnter={(e) => {
        if (disabled) return;
        if (variant === "primary")
          e.currentTarget.style.background = "var(--accent-hover)";
        else if (variant === "ghost")
          e.currentTarget.style.background = "var(--bg-subtle)";
        else if (variant === "secondary")
          e.currentTarget.style.background = "var(--bg-subtle)";
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        if (variant === "primary")
          e.currentTarget.style.background = "var(--accent-primary)";
        else if (variant === "ghost")
          e.currentTarget.style.background = "transparent";
        else if (variant === "secondary")
          e.currentTarget.style.background = "var(--bg-surface)";
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

// Compute a stable signature of a voce. Excludes qty (no impact on match)
// and excludes the local _matching/_match_attempted flags.
function matchSignature(v) {
  return JSON.stringify({
    f: v.famiglia,
    e: v.parametri_estratti || {},
    i: v.parametri_inferiti || {},
    m: v.parametri_mancanti_critici || [],
    c: !!v._is_custom,
  });
}

export default function WorkbenchPane({
  session,
  loading,
  saving,
  matching,
  blocchi,
  onClose,
  onPatch,
  onMatch,
  onOpenMessage,
  onReloadSession,
  onVociUpdate,
  toast,
}) {
  const [voci, setVoci] = useState(session?.voci || []);
  const inflightRef = useRef({}); // idx -> true when match-voce request is pending
  const sigRef = useRef({}); // idx -> last signature we sent to match-voce
  const gridRef = useRef(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  // R11/B — Auto-confirm ESATTI: state must be declared BEFORE any early
  // return below (Rules of Hooks).
  const [autoConfirmRunning, setAutoConfirmRunning] = useState(false);
  // R11.b — Learning rule modal state
  const [learningModalOpen, setLearningModalOpen] = useState(false);
  const [teachingVoce, setTeachingVoce] = useState(null);
  const { user } = useAuth();
  const canApprove = hasRole(user, "titolare", "super_admin");
  const canCreateActiveRule = hasRole(user, "titolare", "super_admin");
  const canTeachVoce = !!user; // anyone authenticated can teach (commercial → pending)
  const clienteEmailDomain = session?.cliente?.email_domain || null;
  const nav = useNavigate();
  const navigate = nav;

  useEffect(() => {
    setVoci(session?.voci || []);
    inflightRef.current = {};
    sigRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session_id, session?.updated_at]);

  // R10 v0.5.8 — Propagate voci to parent so TotaleOffertaCard (in cliente
  // pane) sees fresh candidati after auto-match completes.
  useEffect(() => {
    onVociUpdate?.(voci);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voci]);

  // R11/A — Bulk auto-match on session open: replace the legacy pattern
  // of N parallel /match-voce calls (which caused 1-2 voci out of 4 to
  // miss matching at first load) with a SINGLE server-side bulk endpoint
  // that loops + persists atomically.
  const bulkMatchFiredRef = useRef(false);
  useEffect(() => {
    if (!session?.session_id) return;
    if (matching) return;
    const sid = session.session_id;
    // Reset the fire-flag whenever the session changes
    // (handled by the [session?.session_id] dep + the inner dedupe below).
    const needsBulk = voci.some(
      (v) =>
        !v._is_custom &&
        !v._is_custom_machined &&
        !v._matching &&
        !v._match_attempted &&
        (v.parametri_mancanti_critici || []).length === 0 &&
        (!v.candidati || v.candidati.length === 0)
    );
    if (!needsBulk) return;
    if (bulkMatchFiredRef.current) return;
    bulkMatchFiredRef.current = true;
    const t = setTimeout(async () => {
      try {
        // Mark candidates as "_matching" for UI feedback
        setVoci((prev) =>
          prev.map((vv) =>
            !vv._is_custom &&
            !vv._is_custom_machined &&
            !vv._match_attempted &&
            (vv.parametri_mancanti_critici || []).length === 0 &&
            (!vv.candidati || vv.candidati.length === 0)
              ? { ...vv, _matching: true }
              : vv
          )
        );
        const updated = await api.matchTutte(sid);
        setVoci(
          (updated?.voci || []).map((v) => ({ ...v, _matching: false }))
        );
      } catch (e) {
        // Fall back to per-voce inflight clearing; UI shows "no match" cells
        setVoci((prev) =>
          prev.map((vv) =>
            vv._matching ? { ...vv, _matching: false, _match_attempted: true } : vv
          )
        );
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voci, session?.session_id, matching]);

  // Reset the bulk-fire ref on session change so reopening triggers a new pass.
  useEffect(() => {
    bulkMatchFiredRef.current = false;
  }, [session?.session_id, session?.updated_at]);

  if (!session && !loading) {
    return (
      <section
        className="flex-1 h-full flex items-center justify-center bg-white"
        style={{ borderRight: "1px solid var(--border-subtle)" }}
        data-testid="workbench-empty"
      >
        <div className="text-center max-w-sm tq-fade-in">
          <EmptyWorkbench
            className="mx-auto mb-6"
            style={{ color: "var(--text-muted)" }}
          />
          <h2
            className="text-[20px] font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            Scrivania pronta
          </h2>
          <p
            className="mt-2 text-[14px] leading-[22px] mx-auto max-w-[380px]"
            style={{ color: "var(--text-secondary)" }}
          >
            Incolla una richiesta per analizzarla. Le voci appariranno qui in
            una griglia editabile.
          </p>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section
        className="flex-1 flex items-center justify-center bg-white"
        style={{ borderRight: "1px solid var(--border-subtle)" }}
        data-testid="workbench-loading"
      >
        <div className="w-72 space-y-3">
          <div
            className="flex items-center justify-center gap-2 text-[14px] font-medium"
            style={{ color: "var(--text-primary)" }}
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Decodifica della richiesta in corso
          </div>
          <div className="h-1 tq-loader-bar rounded-full" />
          <div
            className="text-center text-[12px]"
            style={{ color: "var(--text-muted)" }}
          >
            Tipicamente impiega 5–15 secondi
          </div>
        </div>
      </section>
    );
  }

  const hasMissing = voci.some(
    (v) => (v.parametri_mancanti_critici || []).length > 0
  );
  const customCount = voci.filter((v) => v._is_custom).length;
  const someStillMatching = voci.some((v) => v._matching);
  // A voce is "covered" if it is custom (manual quote, excluded from CSV)
  // or it has at least one candidate. Voci waiting on missing params count as not covered.
  const allCovered =
    voci.length > 0 &&
    voci.every(
      (v) =>
        v._is_custom ||
        (v.candidati && v.candidati.length > 0)
    );
  // CSV is exportable when at least one non-custom voce has been matched.
  const exportableCount = voci.filter(
    (v) => !v._is_custom && v.candidati && v.candidati.length > 0
  ).length;
  const csvDisabled =
    hasMissing || matching || someStillMatching || !allCovered || exportableCount === 0;

  // Update a voce, resetting match state when match-relevant fields changed.
  const updateVoce = (idx, newVoce) =>
    setVoci((prev) =>
      prev.map((v, i) => {
        if (i !== idx) return v;
        const oldSig = matchSignature(v);
        const merged = { ...newVoce, idx: i };
        const newSig = matchSignature(merged);
        if (oldSig !== newSig) {
          // Match-relevant change → drop candidati and force re-match
          delete sigRef.current[idx];
          return {
            ...merged,
            candidati: [],
            _match_attempted: false,
            _matching: false,
          };
        }
        return merged;
      })
    );
  const addVoce = () =>
    setVoci((prev) => [
      ...prev,
      {
        idx: prev.length,
        famiglia: "F05",
        sotto_tipo: "",
        quantita: 1,
        parametri_estratti: {},
        parametri_inferiti: {},
        parametri_mancanti_critici: ["P01_DN", "P06_PN", "P10_materiale"],
        stato: "incomplete",
        candidati: [],
        _match_attempted: false,
      },
    ]);
  const removeVoce = (idx) => {
    delete sigRef.current[idx];
    delete inflightRef.current[idx];
    setVoci((prev) =>
      prev.filter((_, i) => i !== idx).map((v, i) => ({ ...v, idx: i }))
    );
  };
  const duplicateVoce = (idx) =>
    setVoci((prev) => {
      const dup = {
        ...prev[idx],
        candidati: [],
        _match_attempted: false,
        _matching: false,
        idx: prev.length,
      };
      return [...prev, dup];
    });
  const markCustom = (idx) =>
    setVoci((prev) =>
      prev.map((v, i) =>
        i === idx
          ? {
              ...v,
              _is_custom: true,
              _matching: false,
              _match_attempted: true,
              candidati: [],
            }
          : v
      )
    );

  /** R5 P0 #2 — apply a listino alternative swap to a custom voce */
  const applyAlternativeSwap = (idx, swapPatch) => {
    setVoci((prev) =>
      prev.map((v, i) => {
        if (i !== idx) return v;
        const nextEstr = { ...(v.parametri_estratti || {}), ...(swapPatch || {}) };
        return {
          ...v,
          parametri_estratti: nextEstr,
          _is_custom: false,
          _match_attempted: false,
          _matching: false,
          candidati: [],
        };
      })
    );
  };
  const selectCandidate = (idx, codice) =>
    setVoci((prev) =>
      prev.map((v, i) =>
        i === idx
          ? {
              ...v,
              candidati: (v.candidati || []).map((c) => ({
                ...c,
                selezionato: c.codice === codice,
              })),
            }
          : v
      )
    );

  // Manual fallback: re-match every voce server-side.
  const recalcAll = async () => {
    if (matching || hasMissing) return;
    // Persist any pending edits first so the server has the latest voci.
    await onPatch({ voci });
    // Reset our local signature cache so post-recalc auto-match is silent.
    sigRef.current = {};
    inflightRef.current = {};
    await onMatch();
  };

  const exportCsv = async () => {
    await onPatch({ voci });
    const sel = voci
      .filter((v) => !v._is_custom && v.candidati && v.candidati.length > 0)
      .map((v) => ({
        voce_idx: v.idx,
        codice:
          v.candidati.find((c) => c.selezionato)?.codice ||
          v.candidati[0].codice,
      }));
    if (sel.length) {
      try {
        await api.selectCandidates(session.session_id, sel);
      } catch (e) {}
    }
    const a = document.createElement("a");
    a.href = api.csvUrl(session.session_id);
    a.download = `techquote_${session.session_id.slice(0, 8)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast?.("CSV scaricato");
  };

  const saveAndClose = async () => {
    await onPatch({ voci });
    toast?.("Salvato");
    onClose();
  };

  // R11/B — Auto-confirm ESATTI: count rows with top candidate ESATTO and
  // status != confirmed and not manual_override. Used for the toolbar button.
  const esattiPendingCount = voci.filter((v) => {
    if (v?.status === "confirmed") return false;
    if (v?.manual_override) return false;
    const top = (v?.candidati || [])[0];
    if (!top) return false;
    return String(top.confidence_level || "").toUpperCase() === "ESATTO";
  }).length;

  const handleAutoConfirmEsatti = async () => {
    if (!session?.session_id || autoConfirmRunning) return;
    const m = voci.filter((v) => {
      const top = (v?.candidati || [])[0];
      const isEsatto =
        top && String(top.confidence_level || "").toUpperCase() === "ESATTO";
      return !isEsatto && !v?.manual_override && v?.status !== "confirmed";
    }).length;
    const proceed = window.confirm(
      `Confermare ${esattiPendingCount} voci ESATTO 100%?\n\n` +
        `Le altre ${m} (PROSSIMO / VARIANTE / non a listino) restano manuali.`
    );
    if (!proceed) return;
    setAutoConfirmRunning(true);
    try {
      const res = await api.autoConfirmEsatti(session.session_id);
      toast?.success?.(
        `Confermate ${res.confirmed} voci ESATTO` +
          (res.skipped_non_esatto
            ? ` (${res.skipped_non_esatto} skip non-ESATTO)`
            : "")
      );
      await onReloadSession?.();
    } catch (e) {
      toast?.error?.(
        e?.response?.data?.detail || "Errore conferma automatica"
      );
    } finally {
      setAutoConfirmRunning(false);
    }
  };

  return (
    <section
      className="flex-1 flex flex-col overflow-hidden bg-white"
      style={{ borderRight: "1px solid var(--border-subtle)" }}
      data-testid="workbench-pane"
    >
      {/* Toolbar */}
      <div
        className="px-4 py-2 flex items-center justify-between gap-3"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        <div className="min-w-0 flex items-center gap-3" data-testid="workbench-breadcrumb">
          <button
            type="button"
            onClick={() => nav("/")}
            className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-slate-100 transition-colors text-[12px]"
            style={{ color: "var(--text-muted)" }}
            data-testid="back-to-offers-btn"
            title="Torna alla dashboard offerte"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Offerte
          </button>
          <span
            className="h-4 w-px"
            style={{ background: "var(--border-subtle)" }}
          />
          <div
            className="text-[12px] truncate shrink"
            style={{ color: "var(--text-secondary)" }}
          >
            <span
              className="font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              {session.cliente?.nome_azienda || "Cliente non identificato"}
            </span>
            <span style={{ color: "var(--text-muted)" }}>
              {" · "}
              {voci.length} {voci.length === 1 ? "voce" : "voci"}
              {session.offer_number && (
                <>
                  {" · "}
                  <span className="font-mono">{session.offer_number}</span>
                </>
              )}
            </span>
          </div>
          {session.workflow_status && (
            <div className="hidden xl:block" data-testid="step-bar-wrapper">
              <StatusStepBar
                status={session.workflow_status}
                canEdit={canApprove}
                onStepClick={async (step) => {
                  try {
                    await offersApi.setStatus(session.session_id, step.targetStatus);
                    toast?.(`Stato → ${step.label}`);
                    onReloadSession?.();
                  } catch (e) {
                    toast?.(e?.response?.data?.detail || "Errore cambio stato");
                  }
                }}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <ToolbarBtn onClick={saveAndClose} disabled={saving} variant="ghost" testId="save-btn">
            <Save className="h-3.5 w-3.5" />
            {saving ? "Salvataggio..." : "Salva"}
          </ToolbarBtn>
          <ToolbarBtn
            onClick={recalcAll}
            disabled={hasMissing || matching || saving}
            variant="ghost"
            testId="recalc-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${matching ? "animate-spin" : ""}`} />
            Ricalcola codici
          </ToolbarBtn>
          {esattiPendingCount > 0 && (
            <ToolbarBtn
              onClick={handleAutoConfirmEsatti}
              disabled={autoConfirmRunning || saving}
              variant="secondary"
              testId="auto-confirm-esatti-btn"
            >
              {autoConfirmRunning ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              Conferma tutti gli ESATTI ({esattiPendingCount})
            </ToolbarBtn>
          )}
          {["approvata", "inviata", "vinta", "persa"].includes(
            session.workflow_status
          ) && (
            <ToolbarBtn
              onClick={() => setPdfOpen(true)}
              variant="secondary"
              testId="generate-pdf-btn"
            >
              <FileText className="h-3.5 w-3.5" />
              Genera PDF
            </ToolbarBtn>
          )}
          {hasMissing && (
            <ToolbarBtn
              onClick={onOpenMessage}
              variant="warning"
              testId="open-message-btn"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Messaggio cliente
            </ToolbarBtn>
          )}
          <ToolbarBtn
            onClick={exportCsv}
            disabled={csvDisabled}
            variant="primary"
            testId="export-csv-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Esporta CSV
          </ToolbarBtn>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors"
                style={{ color: "var(--text-muted)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--bg-subtle)";
                  e.currentTarget.style.color = "var(--text-primary)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "var(--text-muted)";
                }}
                data-testid="toolbar-kebab"
                aria-label="Altre azioni"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={() => {
                  gridRef.current?.resetWidths?.();
                  toast?.("Larghezze colonne ripristinate");
                }}
                data-testid="reset-widths-item"
              >
                <Columns3 className="h-3.5 w-3.5 mr-2" />
                Ripristina larghezze colonne
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors"
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bg-subtle)";
              e.currentTarget.style.color = "var(--text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--text-muted)";
            }}
            data-testid="close-btn"
            aria-label="Chiudi"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-auto tq-scroll px-3 py-3 space-y-3 tq-fade-in"
        style={{ background: "var(--bg-app)" }}
      >
        {canApprove && (
          <ApprovalBanner session={session} onRefresh={onReloadSession} />
        )}
        <BlocchiClienteBanner
          blocchi={blocchi}
          sessionId={session?.session_id}
        />
        <IpotesiSoftwareCard
          voci={voci}
          settore={session?.cliente?.settore_presunto}
        />
        <VociGrid
          ref={gridRef}
          voci={voci}
          onUpdateVoce={updateVoce}
          onAddVoce={addVoce}
          onRemoveVoce={removeVoce}
          onDuplicateVoce={duplicateVoce}
          onMarkCustom={markCustom}
          onSelectCandidate={selectCandidate}
          onRequestMatch={onMatch}
          onTeachVoce={(v) => {
            setTeachingVoce(v);
            setLearningModalOpen(true);
          }}
          onLearnedRuleClick={(ruleId) => {
            // Navigate to admin if titolare/super_admin, else show info toast
            if (canCreateActiveRule) {
              navigate(`/admin/learning-rules#${ruleId}`);
            } else {
              toast.info("Regola appresa. Solo titolare/super_admin può modificarla.");
            }
          }}
          canTeach={canTeachVoce}
        />
        <LearningRuleModal
          open={learningModalOpen}
          onOpenChange={(o) => {
            setLearningModalOpen(o);
            if (!o) setTeachingVoce(null);
          }}
          voce={teachingVoce}
          sessionContext={{
            cliente_email_domain: clienteEmailDomain,
            settore_code: null,
          }}
          canCreateActive={canCreateActiveRule}
          onCreate={async (payload) => {
            const created = await api.post("/learning-row-rules", payload);
            toast.success(
              canCreateActiveRule
                ? "Regola creata e attiva ✓"
                : "Regola proposta — in attesa di approvazione",
            );
            // Re-run match-tutte to apply immediately if active
            if (canCreateActiveRule) {
              try {
                await onMatch?.();
              } catch (e) { /* non-blocking */ }
            }
            return created;
          }}
        />
        {customCount > 0 && (
          <div
            className="rounded-md px-4 py-3 text-[13px] space-y-2"
            style={{
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              color: "#92400E",
            }}
            data-testid="custom-warning"
          >
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#B45309" }} />
              <div className="flex-1">
                <div className="font-semibold">
                  {customCount}{" "}
                  {customCount === 1
                    ? "voce richiede lavorazione custom"
                    : "voci richiedono lavorazione custom"}
                  {" "}— preventivo manuale
                </div>
                <div className="text-[12px] mt-0.5" style={{ color: "#92400E" }}>
                  Queste voci non hanno corrispondenza diretta a listino e
                  vengono escluse dal CSV. Prova a cercare alternative prima
                  di passare al manuale.
                </div>
              </div>
            </div>
            {/* R5 — per-voce alternatives popover */}
            <ul className="space-y-1 text-[12px]">
              {voci
                .map((v, i) => ({ v, i }))
                .filter(({ v }) => v._is_custom)
                .map(({ v, i }) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-2 rounded px-2 py-1"
                    style={{ background: "rgba(255,255,255,0.6)" }}
                    data-testid={`custom-voce-row-${i}`}
                  >
                    <span className="truncate">
                      <span className="font-semibold">#{i + 1}</span>{" "}
                      {v.sotto_tipo || v.famiglia}
                    </span>
                    <ListinoAlternativesPopover
                      voce={v}
                      triggerLabel="Alternative"
                      onSwap={(patch) => applyAlternativeSwap(i, patch)}
                    />
                  </li>
                ))}
            </ul>
          </div>
        )}
        {hasMissing && (
          <div
            className="text-[12px] px-1"
            style={{ color: "var(--text-muted)" }}
            data-testid="missing-hint"
          >
            Le celle rosse sono parametri da chiarire con il cliente — usa
            &quot;Messaggio cliente&quot; in alto.
          </div>
        )}
      </div>
      {pdfOpen && (
        <PdfPreviewModal
          offerId={session.session_id}
          offerNumber={session.offer_number}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </section>
  );
}
